
import { NoseurObject } from "@rivtn/noseur";
import { BaseService, IEarningSource, IFactorResponse, SanitizedResponse, TransactionSourceType, TransactionStatus, TransactionType } from "../BaseService";
import { Utils } from "../../utils";

export enum PayoutSchedule {

    QUARTERLY = "QUARTERLY",
    EVERY_FRIDAY = "EVERY_FRIDAY",
    EVERY_2_WEEKS = "EVERY_2_WEEKS",
    LAST_FRIDAY_OF_EVERY_MONTH = "LAST_FRIDAY_OF_EVERY_MONTH",

}

export enum PayoutMethodDetailEntryType {

    TEXT = "TEXT",
    NUMBER = "NUMBER",
    OPTIONS = "OPTIONS",

}

export interface IMemberCurrencyBalance {
    external_id: string;
    currency_code: string;
    pending_amount: number;
    available_amount: number;
    withdrawn_amount: number;
    in_progress_amount: number;
    percentage_increase: number;
    last_withdrawal_date: string;
    last_withdrawn_amount: number;
    latest_in_progress_amount: number;
}

export interface ITransaction {
    amount: number;
    created_at: string;
    tax_amount: number;
    external_id: string;
    description: string;
    currency_code: string;
    type: TransactionType;
    payable_amount: number;
    platform_amount: number;
    status: TransactionStatus;
    source_data?: IEarningSource;
    extra_data?: NoseurObject<any>;
    source_type: TransactionSourceType;
}

export interface IPlatformPayoutMethodDetailEntry {
    label?: string;
    title: string;
    type: PayoutMethodDetailEntryType;
    options?: {
        icon?: string;
        label: string;
        value: string;
    }[];
}

export interface IPlatformPayoutMethod {
    name: string;
    external_id: string;
    logo_location: string;
    currency_code: string;
    recommended?: boolean;
    information: string[];
    payout_currency_code: string;
    withdrawal_fee_percentage: number;
    withdrawal_maximum_amount: number;
    withdrawal_minimum_amount: number;
    details: {
        title: string;
        entries: IPlatformPayoutMethodDetailEntry[];
    };
}

export interface IMemberPayoutMethod {
    is_default: boolean;
    external_id: string;
    currency_code: string;
    extra_data: NoseurObject<any>;
    payout_details: NoseurObject<any>;
    platform_payout_method: IPlatformPayoutMethod;
}

export interface IExchangeRate {
    exchange_rate: number;
    base_currency: string;
    exchange_currency: string;
}

export interface IMemberWithdrawalSetting {
    min_amount: number;
    currency_code: string;
    reserve_amount: number;
    extra_data: NoseurObject<any>;
    member_payout_method_id: string;
    schedule_interval: PayoutSchedule;
    payout_method: IMemberPayoutMethod;
}

export class EarningService extends BaseService {

    protected static instance: EarningService;

    private constructor() {
        super();
    }

    static getInstance() {
        if (!EarningService.instance) EarningService.instance = new EarningService();
        return EarningService.instance;
    }

    queryCurrencyBalance(params: NoseurObject<string | number> = { page: 1, size: 10 }): Promise<any> {
        return this.report(this.transport.get("/v1/member/currency/balance", { query: this.normalizeParams(params) }));
    }

    getCurrencyBalance(currency: string, params: NoseurObject<string | number | boolean> = { page: 1, size: 10 }): Promise<any> {
        return this.report(this.transport.get(`/v1/member/currency/balance/get/${currency}`, { query: this.normalizeParams(params) }));
    }

    queryCurrencyAnalytics(params: NoseurObject<string | number> = { page: 1, size: 10 }): Promise<any> {
        const { supplementParams, graphDataKey, labelKey } = this.getIdealStatQueryForDateRange(params.from as string, params.to as string);
        return this.report(this.transport.get(`/v1/member/transaction/stat`, { query: this.normalizeParams({ ...params, ...supplementParams }) }), (response: SanitizedResponse<any>) => {
            const graphData = response.sanitized[graphDataKey];
            response.apiResponse.data = {
                labels: graphData?.map((g: any) => g[labelKey]),
                datasets: [
                    {
                        label: "Withdrawal",
                        data: graphData?.map((g: any) => ((g["payable_amount_debited"] ?? 0) / 1000000))
                    },
                    {
                        label: "Earnings",
                        data: graphData?.map((g: any) => ((g["payable_amount_credited"] ?? 0) / 1000000))
                    }
                ],
            };
        });
    }

    queryTransactions(params: NoseurObject<string | number | any[]> = { page: 1, size: 10 }): Promise<any> {
        return this.report(this.transport.get("/v1/member/transaction", { query: this.normalizeParams(params) }));
    }

    queryPayoutMethods(params: NoseurObject<string | number | any[]> = { page: 1, size: 10 }): Promise<SanitizedResponse<IPlatformPayoutMethod>> {
        return this.report(this.transport.get("/v1/platform/payout/method", { query: this.normalizeParams(params) }));
    }

    getExchangeRate(baseCurrency: string, exchangeCurrency: string): Promise<SanitizedResponse<IExchangeRate>> {
        if (baseCurrency === exchangeCurrency) {
            return this.mockResolve(100, {
                data: {
                    succuss: true,
                    message: "Success",
                    data: {
                        exchange_rate: 1000000,
                        base_currency: baseCurrency,
                        exchange_currency: exchangeCurrency
                    }
                }
            });
        }
        return this.mockResolve(1000, {
            data: {
                succuss: true,
                message: "Success",
                data: {
                    exchange_rate: 1650000,
                    base_currency: baseCurrency,
                    exchange_currency: exchangeCurrency
                }
            }
        });
        return this.report(this.transport.get(`/v1/exchange/rate/${baseCurrency}/${exchangeCurrency}`));
    }

    queryMemberPayoutMethods(params: NoseurObject<string | number | any[]> = { page: 1, size: 10 }): Promise<SanitizedResponse<IMemberPayoutMethod>> {
        return this.report(this.transport.get("/v1/member/payout/method", { query: this.normalizeParams(params) }));
    }

    createMemberPayoutMethod(payload: any): Promise<SanitizedResponse<IMemberPayoutMethod>> {
        return this.report(this.transport.post("/v1/member/payout/method", payload));
    }

    setDefaultMemberPayoutMethod(id: string): Promise<SanitizedResponse<IMemberPayoutMethod>> {
        return this.report(this.transport.put(`/v1/member/payout/method/${id}/default`, {}));
    }

    deleteMemberPayoutMethod(id: string): Promise<SanitizedResponse<IMemberPayoutMethod>> {
        return this.report(this.transport.delete(`/v1/member/payout/method/${id}`));
    }

    getWithdrawalSetting(): Promise<SanitizedResponse<IMemberWithdrawalSetting>> {
        return this.report(this.transport.get(`/v1/member/withdrawal/setting`));
    }

    updateWithdrawalSetting(payload: any): Promise<SanitizedResponse<IMemberWithdrawalSetting>> {
        return this.report(this.transport.put(`/v1/member/withdrawal/setting`, payload));
    }

    deleteWithdrawalSetting(): Promise<SanitizedResponse<IMemberWithdrawalSetting>> {
        return this.report(this.transport.delete(`/v1/member/withdrawal/setting`));
    }

    requestWithdrawalToken(payload: string): Promise<SanitizedResponse<IFactorResponse>> {
        return this.report(this.transport.post(`/v1/member/withdrawal/token`, payload));
    }

    initiateWithdrawal(payload: string): Promise<SanitizedResponse<IFactorResponse>> {
        return this.report(this.transport.post(`/v1/member/withdrawal/initiate`, payload));
    }

    static buildTransactionSourceLink(transaction: { source_data?: IEarningSource; source_type?: TransactionSourceType; }) {
        if (transaction.source_type === TransactionSourceType.TOURNAMENT && transaction.source_data?.data?.public_id) {
            return Utils.buildPublicUrl("TOURNAMENT", transaction.source_data?.data?.public_id);
        }
        return;
    }

    static TEST_EARNINGS: any[] = [
        {
            amount: 509960000,
            external_id: "1",
            currency_code: "NGN",
            percentage_increase: 5.20
        },
        {
            amount: 10007880,
            external_id: "2",
            currency_code: "INR",
            percentage_increase: -2.90
        },
        {
            amount: 457200800,
            external_id: "3",
            currency_code: "USD",
            percentage_increase: 9.35
        },
        {
            amount: 12232023,
            external_id: "4",
            currency_code: "GBP",
            percentage_increase: 0.00
        },
        {
            amount: 9898,
            external_id: "5",
            currency_code: "GHS",
            percentage_increase: -10.00
        },
        {
            amount: 67000090,
            external_id: "6",
            currency_code: "KES",
            percentage_increase: 90.00
        },
        {
            amount: 40797,
            external_id: "7",
            currency_code: "ZAR",
            percentage_increase: -0.50
        },
    ];

}
